import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useUpdateWebhookConfig, type WebhookConfig } from '@/hooks/useWebhookConfigs';

const formSchema = z.object({
  display_name: z.string().min(1, 'Obrigatório'),
  description: z.string().nullable(),
  default_channel: z.string().nullable(),
  default_source: z.string().nullable(),
  default_stage: z.string().nullable(),
  default_business_unit: z.string().nullable(),
  default_deals_source: z.string().nullable(),
  default_deals_channel: z.string().nullable(),
  default_acquisition_channel: z.string().nullable(),
  field_mapping_json: z.string().refine(
    (val) => {
      if (!val) return true;
      try { JSON.parse(val); return true; } catch { return false; }
    },
    { message: 'JSON inválido' }
  ),
});

type FormValues = z.infer<typeof formSchema>;

interface Props {
  config: WebhookConfig | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WebhookConfigModal({ config, open, onOpenChange }: Props) {
  const updateMutation = useUpdateWebhookConfig();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: config ? {
      display_name: config.display_name,
      description: config.description ?? '',
      default_channel: config.default_channel ?? '',
      default_source: config.default_source ?? '',
      default_stage: config.default_stage ?? '',
      default_business_unit: config.default_business_unit ?? '',
      default_deals_source: config.default_deals_source ?? '',
      default_deals_channel: config.default_deals_channel ?? '',
      default_acquisition_channel: config.default_acquisition_channel ?? '',
      field_mapping_json: config.field_mapping ? JSON.stringify(config.field_mapping, null, 2) : '',
    } : undefined,
  });

  const onSubmit = (values: FormValues) => {
    if (!config) return;
    
    const fieldMapping = values.field_mapping_json
      ? JSON.parse(values.field_mapping_json)
      : null;

    updateMutation.mutate(
      {
        id: config.id,
        display_name: values.display_name,
        description: values.description || null,
        default_channel: values.default_channel || null,
        default_source: values.default_source || null,
        default_stage: values.default_stage || null,
        default_business_unit: values.default_business_unit || null,
        default_deals_source: values.default_deals_source || null,
        default_deals_channel: values.default_deals_channel || null,
        default_acquisition_channel: values.default_acquisition_channel || null,
        field_mapping: fieldMapping,
      },
      { onSuccess: () => onOpenChange(false) }
    );
  };

  if (!config) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configurar {config.display_name}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="display_name" render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome de exibição</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="default_business_unit" render={({ field }) => (
                <FormItem>
                  <FormLabel>Business Unit padrão</FormLabel>
                  <FormControl><Input {...field} value={field.value ?? ''} /></FormControl>
                </FormItem>
              )} />
            </div>

            <FormField control={form.control} name="description" render={({ field }) => (
              <FormItem>
                <FormLabel>Descrição</FormLabel>
                <FormControl><Input {...field} value={field.value ?? ''} /></FormControl>
              </FormItem>
            )} />

            <div className="grid grid-cols-3 gap-4">
              <FormField control={form.control} name="default_channel" render={({ field }) => (
                <FormItem>
                  <FormLabel>Canal padrão</FormLabel>
                  <FormControl><Input {...field} value={field.value ?? ''} /></FormControl>
                </FormItem>
              )} />
              <FormField control={form.control} name="default_source" render={({ field }) => (
                <FormItem>
                  <FormLabel>Fonte padrão</FormLabel>
                  <FormControl><Input {...field} value={field.value ?? ''} /></FormControl>
                </FormItem>
              )} />
              <FormField control={form.control} name="default_stage" render={({ field }) => (
                <FormItem>
                  <FormLabel>Estágio padrão</FormLabel>
                  <FormControl><Input {...field} value={field.value ?? ''} /></FormControl>
                </FormItem>
              )} />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FormField control={form.control} name="default_deals_source" render={({ field }) => (
                <FormItem>
                  <FormLabel>Deals Source</FormLabel>
                  <FormControl><Input {...field} value={field.value ?? ''} /></FormControl>
                </FormItem>
              )} />
              <FormField control={form.control} name="default_deals_channel" render={({ field }) => (
                <FormItem>
                  <FormLabel>Deals Channel</FormLabel>
                  <FormControl><Input {...field} value={field.value ?? ''} /></FormControl>
                </FormItem>
              )} />
              <FormField control={form.control} name="default_acquisition_channel" render={({ field }) => (
                <FormItem>
                  <FormLabel>Acquisition Channel</FormLabel>
                  <FormControl><Input {...field} value={field.value ?? ''} /></FormControl>
                </FormItem>
              )} />
            </div>

            <FormField control={form.control} name="field_mapping_json" render={({ field }) => (
              <FormItem>
                <FormLabel>Field Mapping (JSON)</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={field.value ?? ''}
                    className="font-mono text-sm min-h-[120px]"
                    placeholder='{"phone": "field_data.phone_number"}'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
